import type { AtomEconomyResult } from './chemistryCalculations';
import { prettyChemistry } from './fuelsSustainability';
import { formatSig } from './gasLaws';
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';
import { routeEconomies, type GreenComparison, type RouteKey } from './synthesisDesignRoutes';

/**
 * Green chemistry Explore (CT-INTERACT): both routes' atom economy with the molar masses
 * substituted, AE = M(desired) ÷ ΣM(reactants) × 100, and the learner's reactant mass split into
 * desired product and by-product mass at 100 % yield. Molar masses come from `calculateAtomEconomy`.
 */
export type GreenExploreInput = 'reactantMassG';

export const GREEN_EXPLORE_FIELDS: ModelField<GreenExploreInput>[] = [
  { key: 'reactantMassG', label: 'Mass of reactants', unit: 'g', min: 1, max: 1000, step: 1 },
];

export const DEFAULT_GREEN_SETUP: Record<GreenExploreInput, number> = { reactantMassG: 100 };

const mm = (value: number) => formatModelNumber(value, 2);
const fmt = (value: number) => formatSig(value, 3);

export interface GreenRouteExplore {
  key: RouteKey;
  name: string;
  equation: string;
  desiredFormula: string;
  percent: number;
  desiredMassG: number;
  byProductMassG: number;
  steps: WorkedStep[];
}

/** Worked atom economy for one route; the substituted numbers are the result's own molar masses. */
export function atomEconomySteps(result: AtomEconomyResult, reactantMassG: number, prefix: string): WorkedStep[] {
  const sumTerms = result.reactants.map((item) => `${item.coefficient} × ${mm(item.molarMass)}`).join(' + ');
  const desiredMassG = (result.percent / 100) * reactantMassG;
  return [
    {
      id: `${prefix}-reactants`,
      title: 'Total molar mass of the reactants',
      formula: 'ΣM(reactants) = Σ coefficient × M',
      working: `${sumTerms} = ${mm(result.reactantMass)} g mol⁻¹ (${result.reactants.map((item) => item.formula).join(', ')})`,
      status: 'done',
    },
    {
      id: `${prefix}-desired`,
      title: 'Molar mass of the desired product',
      formula: 'M(desired) = coefficient × M',
      working: `${result.desired.coefficient} × ${mm(result.desired.molarMass)} = ${mm(result.desiredMass)} g mol⁻¹ (${result.desired.formula})`,
      status: 'done',
    },
    {
      id: `${prefix}-ae`,
      title: 'Atom economy',
      formula: 'AE = M(desired) ÷ ΣM(reactants) × 100',
      working: `${mm(result.desiredMass)} ÷ ${mm(result.reactantMass)} × 100 = ${fmt(result.percent)} %`,
      status: 'done',
    },
    {
      id: `${prefix}-mass`,
      title: 'Your reactant mass at 100 % yield',
      formula: 'desired mass = AE ÷ 100 × reactant mass',
      working: `${fmt(result.percent)} % × ${fmt(reactantMassG)} g = ${fmt(desiredMassG)} g ${result.desired.formula}; by-products ${fmt(reactantMassG)} − ${fmt(desiredMassG)} = ${fmt(reactantMassG - desiredMassG)} g`,
      status: 'done',
    },
  ];
}

/** Both routes for the learner's reactant mass. Throws on invalid input or a failed equation check (fail closed). */
export function calculateGreenExplore(
  entry: GreenComparison,
  values: Record<GreenExploreInput, number>,
): Record<RouteKey, GreenRouteExplore> {
  const error = firstModelError(GREEN_EXPLORE_FIELDS, values);
  if (error) throw new Error(error);
  const economies = routeEconomies(entry);
  if (!economies) throw new Error('This comparison failed its equation checks.');
  const build = (key: RouteKey): GreenRouteExplore => {
    const { route, result } = economies[key];
    const desiredMassG = (result.percent / 100) * values.reactantMassG;
    return {
      key,
      name: route.name,
      equation: prettyChemistry(route.equation),
      desiredFormula: route.desiredFormula,
      percent: result.percent,
      desiredMassG,
      byProductMassG: values.reactantMassG - desiredMassG,
      steps: atomEconomySteps(result, values.reactantMassG, `route-${key.toLowerCase()}`),
    };
  };
  return { A: build('A'), B: build('B') };
}

// Teaching goal (CT-INTERACT C7) ----------------------------------------------------------------

export type GreenExploreResult = Record<RouteKey, GreenRouteExplore>;

/** The route with the higher and the lower atom economy; route B counts as lower on a tie. */
export function greenerAndOther(routes: GreenExploreResult): { greener: GreenRouteExplore; other: GreenRouteExplore } {
  return routes.A.percent >= routes.B.percent ? { greener: routes.A, other: routes.B } : { greener: routes.B, other: routes.A };
}

const MATCH_TOLERANCE = 0.02;

export const GREEN_QUESTION = 'Why can a reaction with a 100 % yield still make waste?';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the two routes' result, never the UI. */
export const GREEN_CHALLENGES: ExploreChallenge<GreenExploreResult>[] = [
  {
    id: 'fifty-grams',
    prompt: 'Make 50 g of the desired product (49–51 g) with the route that has the higher atom economy.',
    hint: 'Reactant mass needed = 50 g ÷ (atom economy ÷ 100).',
    isMet: (routes) => {
      const { greener } = greenerAndOther(routes);
      return greener.desiredMassG >= 49 && greener.desiredMassG <= 51;
    },
    why: 'At 100 % atom economy, 50 g of reactants become 50 g of product because every reactant atom ends up in it. At 57.6 % you would need 87 g.',
  },
  {
    id: 'waste-over-100',
    prompt: 'Make the lower-atom-economy route throw away more than 100 g of by-products.',
    hint: 'By-products are (100 − atom economy) % of the reactant mass.',
    isMet: (routes) => greenerAndOther(routes).other.byProductMassG > 100,
    why: 'Atom economy is fixed by the balanced equation, so every batch sends the same share to by-products: fermentation always loses 48.9 % as CO₂, even at 100 % yield.',
  },
  {
    id: 'match-greener',
    prompt: 'Feed the lower-atom-economy route enough reactant to make as much product as the greener route makes from 100 g (within 2 %).',
    hint: 'The greener route makes (its atom economy) g from 100 g; divide that by the other route’s atom economy ÷ 100.',
    isMet: (routes) => {
      const { greener, other } = greenerAndOther(routes);
      if (greener.percent <= other.percent) return false;
      return Math.abs(other.desiredMassG - greener.percent) <= MATCH_TOLERANCE * greener.percent;
    },
    why: 'Fermentation needs about 196 g of glucose for the 100 g of ethanol that hydration makes from 100 g. Yield measures product lost in practice; atom economy measures atoms lost by the equation itself, so both matter.',
  },
];
